import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useSaveBraintreePaymentTemporarily, useValidationResult } from 'src/app/pages/promote/shared/hooks';
import type { PaypalButton } from 'src/app/shared/components/payments';

import { useSharedHooks } from '../../../hooks/useSharedHooks';

type Result = Parameters<typeof PaypalButton>[0];

export function usePaypal(): Result {
	const [{ amount, email, formState, paymentMethodSaveOptions }] = usePromotePageState();
	const {
		data: {
			guestUserData,
			currency: { code: currencyCode },
			featureConfigs: { useDonationLanguage },
		},
	} = usePromotePageData();

	const { inProgress, ...rest } = useSharedHooks();
	const validationResult = useValidationResult();

	const track = useTracking();
	const savePaypalTemporarily = useSaveBraintreePaymentTemporarily();
	const { translate } = useI18n();
	const flow =
		paymentMethodSaveOptions.shouldSavePaymentMethod || savePaypalTemporarily
			? ('vault' as paypal.FlowType.Vault)
			: ('checkout' as paypal.FlowType.Checkout);

	const disabled = useMemo(() => {
		return formState.status === 'loading';
	}, [formState]);

	return {
		...rest,
		trackLoadStartTime: () => {
			void track('pp_paypal_button_loading_start_time');
		},
		trackLoadEndTime: (duration: number) => {
			void track('pp_paypal_button_loading_end_time', { duration });
		},
		email,
		firstName: guestUserData?.firstName,
		lastName: guestUserData?.lastName,
		flow,
		currency: currencyCode,
		disabled,
		loading: inProgress,
		paymentMethodSaveOptions,
		shouldEnableButton: validationResult('paypal').valid,
		...(flow === 'checkout' && { amount }),
		...(flow === 'vault' && {
			billingAgreementDescription: useDonationLanguage
				? translate('corgi.payments.paypal_billing_agreement_description_donation')
				: translate('corgi.payments.paypal_billing_agreement_description'),
		}),
		savePaypalTemporarily,
	};
}
